import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface NextAction {
  id: string;
  action: string;
  owner: string;
}

export default function PriorityStatementPage() {
  const [priority1, setPriority1] = useState('');
  const [priority2, setPriority2] = useState('');
  const [priority3, setPriority3] = useState('');
  const [cutList, setCutList] = useState('');
  const [criteria, setCriteria] = useState('');
  const [nextActions, setNextActions] = useState<NextAction[]>([
    { id: '1', action: '', owner: '' },
    { id: '2', action: '', owner: '' },
    { id: '3', action: '', owner: '' },
  ]);
  const [owners, setOwners] = useState('');
  const [date, setDate] = useState('');
  const [reviewDate, setReviewDate] = useState('');

  const updateAction = (id: string, field: keyof NextAction, value: string) => {
    setNextActions(nextActions.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  return (
    <TaskPage
      phase="decide"
      title="Priority Statement"
      pageNumber={6}
      instruction="Commit to it. Complete the template: In the next 30 days, our top 3 priorities are... We will not pursue... We chose these because..."
      whyItMatters="Plain-language template reduces ambiguity and support burden; shipping a statement creates social commitment."
      coachRail={{
        timing: "10-20 minutes",
        tips: "Keep it clear and concise. This becomes your commitment statement.",
        debrief: "Does this statement feel right? Is it clear what you're committing to?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Priority Statement Template</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-[var(--text-sec)] mb-2">In the next 30 days, our top 3 priorities are:</p>
              <TextInput
                value={priority1}
                onChange={(e) => setPriority1(e.target.value)}
                placeholder="Priority 1"
                className="mb-2"
              />
              <TextInput
                value={priority2}
                onChange={(e) => setPriority2(e.target.value)}
                placeholder="Priority 2"
                className="mb-2"
              />
              <TextInput
                value={priority3}
                onChange={(e) => setPriority3(e.target.value)}
                placeholder="Priority 3"
              />
            </div>
            <div>
              <p className="text-sm text-[var(--text-sec)] mb-2">We will not pursue:</p>
              <TextArea
                value={cutList}
                onChange={(e) => setCutList(e.target.value)}
                placeholder="List what you're cutting..."
                rows={3}
              />
            </div>
            <div>
              <p className="text-sm text-[var(--text-sec)] mb-2">We chose these because:</p>
              <TextArea
                value={criteria}
                onChange={(e) => setCriteria(e.target.value)}
                placeholder="Cite your criteria..."
                rows={3}
              />
            </div>
          </div>
        </div>

        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">First Next Actions (do within 48 hours)</h3>
          <div className="space-y-3">
            {nextActions.map((action) => (
              <div key={action.id} className="bg-[var(--bg-raised)] border border-[var(--slate)] rounded p-3">
                <div className="grid grid-cols-2 gap-3">
                  <TextInput
                    value={action.action}
                    onChange={(e) => updateAction(action.id, 'action', e.target.value)}
                    placeholder="Action"
                  />
                  <TextInput
                    value={action.owner}
                    onChange={(e) => updateAction(action.id, 'owner', e.target.value)}
                    placeholder="Owner"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-6">
          <h3 className="text-lg font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-4">Sign & Share</h3>
          <div className="grid grid-cols-3 gap-4">
            <TextInput
              label="Owner(s)"
              value={owners}
              onChange={(e) => setOwners(e.target.value)}
              placeholder="Who owns this?"
            />
            <TextInput
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <TextInput
              label="Review checkpoint"
              type="date"
              value={reviewDate}
              onChange={(e) => setReviewDate(e.target.value)}
            />
          </div>
        </div>

        <Callout type="tip" phase="decide">
          <p className="text-sm">Share this statement with stakeholders. Make it visible. This creates social commitment and accountability.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






